import { useAppContext } from '@contexts/app';
import { useQueryClient } from '@tanstack/react-query';
import { appRoutes, jobIdRoute, queryKeys } from '@utils/consts';
import { useLocation, useParams } from 'react-router';
import { JobRefetchOptions, UpdateStagesProps } from './types';
import { useQueryParams } from '@hooks/queryParams';
import { JobStagesRespSuccess } from '@services/jobs/stages/types';
import { JobStage, JobSubtask, JobTask } from '@root/globalTypes';

export const useJobRefetch = (options: JobRefetchOptions = {}) => {
  const { isForeman } = useAppContext();

  const { pathname } = useLocation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const {
    params: { type },
  } = useQueryParams();

  const onDeck = pathname.includes(appRoutes.hotSeat) || pathname.includes(appRoutes.onDeck);
  const workload = pathname.includes(appRoutes.workload);
  const jobs = pathname.includes(appRoutes.jobs);
  const job = pathname.endsWith(appRoutes.job(id || ''));
  const materials = pathname.includes(jobIdRoute.materials);
  const documents = pathname.includes(jobIdRoute.documents);
  const questionnaires = pathname.includes(jobIdRoute.questionnaires);
  const workProgress = pathname.includes(jobIdRoute.workProgress);

  const refetchActionQueries = async () => {
    if (isForeman) await queryClient.invalidateQueries({ queryKey: queryKeys.foremanTask });
    if (onDeck) await queryClient.invalidateQueries({ queryKey: queryKeys.onDeck });
    if (jobs) await queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    if (job) await queryClient.invalidateQueries({ queryKey: [...queryKeys.job, id] });
    if (materials) await queryClient.invalidateQueries({ queryKey: queryKeys.materials });
    if (documents) await queryClient.invalidateQueries({ queryKey: queryKeys.documents });
    if (questionnaires) {
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.questionnaire, id, options.taskId],
      });
    }
    if (documents || materials || workProgress || questionnaires) {
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.jobStages, id] });
    }
  };

  const refetchScheduleQueries = async () => {
    const { schedule } = options;
    if (!schedule) return;

    if (schedule.type === 'internal') {
      if (workload) {
        if (['create', 'remove'].includes(schedule.action))
          await queryClient.invalidateQueries({ queryKey: queryKeys.workloadCalendarSidebar });
        if (type === 'two-week')
          await queryClient.invalidateQueries({ queryKey: queryKeys.workloadTwoWeeklyCalendar });
        if (type === 'month')
          await queryClient.invalidateQueries({
            queryKey: queryKeys.workloadMonthlyCalendar,
          });
        if (!type || type === 'week')
          await queryClient.invalidateQueries({ queryKey: queryKeys.workloadWeeklyCalendar });
      }
    }
  };

  const getStages = (jobId?: string) =>
    queryClient.getQueryData<JobStagesRespSuccess>([...queryKeys.jobStages, jobId || id])
      ?.records || [];

  const restoreStages = (jobId?: string, records?: JobStage[]) => {
    queryClient.setQueryData([...queryKeys.jobStages, jobId || id], { success: true, records });
  };

  const updateStages = async ({ jobId, stage, task, subtask }: UpdateStagesProps) => {
    await queryClient.cancelQueries({ queryKey: [...queryKeys.jobStages, jobId || id] });
    const stages = getStages(jobId);

    queryClient.setQueryData<JobStagesRespSuccess>(
      [...queryKeys.jobStages, jobId || id],
      (prev) => {
        if (!prev) return;
        return {
          ...prev,
          records: prev.records.map((a) => {
            const newStage = a.id === stage?.id ? { ...a, ...stage } : a;
            return {
              ...newStage,
              tasks: newStage.tasks.map((b) => ({
                ...b,
                tasks: b.tasks.map((c) => {
                  const newTask = c.id === task?.id ? { ...c, ...task } : c;
                  return {
                    ...newTask,
                    subtasks: newTask.subtasks?.map((d) =>
                      d.id === subtask?.id ? { ...d, ...subtask } : d,
                    ),
                  };
                }),
              })),
            };
          }),
        };
      },
    );

    return stages;
  };

  const getTask = (jobId?: string, taskId?: string) => {
    let task: JobTask | undefined;
    const stages = getStages(jobId);

    stages.forEach((a) =>
      a.tasks.forEach((b) =>
        b.tasks.forEach((c) => {
          if (c.id === taskId) task = c;
        }),
      ),
    );
    return task;
  };

  const getSubtask = (jobId?: string, subtaskId?: string) => {
    let subtask: JobSubtask | undefined;
    const stages = getStages(jobId);

    stages.forEach((a) =>
      a.tasks.forEach((b) =>
        b.tasks.forEach((c) => {
          c.subtasks?.forEach((d) => {
            if (d.id === subtaskId) subtask = d;
          });
        }),
      ),
    );
    return subtask;
  };

  return {
    refetch: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      await refetchActionQueries();
      await refetchScheduleQueries();
    },
    task: {
      get: getTask,
    },
    subtask: {
      get: getSubtask,
    },
    stages: {
      get: getStages,
      update: updateStages,
      restore: restoreStages,
    },
  };
};
